import type { Option } from "@tento-lona/sheets";
import { DateTime, NaiveDate, Utc } from "@tento-chrono";
import { nonnull } from "@tento-core/assert";
import { Network } from "../network.ts";
import type { Actor } from "../invalidation.ts";
import type { CellValue, TimepointCellEntry, Json } from "@tento-lona/sheets";
import { CellEntry, CellId } from "@tento-lona/sheets";
import { SortedDeque } from "@tento-core/sorted-deque";
import {
  requireDateTimeRange,
  requireNaiveDateRange,
  requireNonEmptyKey,
} from "./_contracts.ts";
import { CellLane } from "@tento-lona/sheets/internal-sdk";
import type { CellCardinality } from "@tento-lona/sheets/platform";

const cellKey = (e: CellEntry): string => `${e.date.type}:${e.date.index}`;

/**
 * What a local cell edit writes into a row's cells: a wire value (`Json`), a
 * decoded `CellValue`, or the tasks client's task snapshot
 * (`Network.TaskAttributes`, with its `DateTime`s and `Rational`), which the
 * task readers read as written. `SheetDataSource.applyCellEditLocal` takes
 * it and passes it on unchanged, through `RowCellCache.applyCellWriteLocal`, to
 * this store.
 */
export type LocalCellValue = Json | CellValue | Network.TaskAttributes;

type ParsedCell = ParsedCell.Standard | ParsedCell.Multi | ParsedCell.Custom;
namespace ParsedCell {
  /** semantic + single → `dateIndex` (date-keyed; one per date). */
  export type Standard = { kind: "standard"; entry: CellEntry };
  /** semantic + multi → `multi` (date-keyed, id-distinguished; many per date). */
  export type Multi = { kind: "multi"; entry: CellEntry & { readonly id: string } };
  /** timepoint (any cardinality) → `customStore` (datetime-keyed by id). */
  export type Custom = { kind: "custom"; entry: TimepointCellEntry };

  /**
   * Route a wire cell onto a lane by the TWO orthogonal axes via `CellLane`,
   * NOT the old `cardinality === "multi" || isDatetime` fusion:
   * - **temporal kind** from the wire `date` STRING shape — a
   *   `NaiveDate.Partial` (ymd/ym/yw/y) is `semantic`, an rfc3339 instant is
   *   `timepoint`.
   * - **cardinality** from the row descriptor.
   * `(semantic,single)→standard`, `(semantic,multi)→multi`, `(timepoint,*)→custom`.
   * So a date-keyed multi cell (an all-day event) now stays in the date lane,
   * id-distinguished — it is no longer forced into the timepoint store. The
   * event optimistic-write path overrides temporal kind from the VALUE
   * (`CellLane.temporalKindOfEvent`); the fetch path trusts the wire shape (the
   * connector emits ymd for all-day, rfc3339 for timed).
   *
   * The server wire contract guarantees an `id` for any cell that lands in an
   * id-keyed lane (multi or custom); a missing id is a contract violation.
   */
  export function parse(
    cell: RowData.Cell,
    cardinality: CellCardinality,
  ): Option<ParsedCell> {
    const partial = NaiveDate.Partial.parseOptional(cell.date);
    const isPartial = partial != null;
    const temporal = isPartial ? "semantic" : "timepoint";
    const lane = CellLane.of(cardinality, temporal);

    if (lane === "custom") {
      if (cell.id == null) {
        throw new Error(
          `RowData: custom-store cell missing id (date="${cell.date}"). ` +
            `Server contract violation — timepoint cells must carry an id.`,
        );
      }
      const dt0 = DateTime.fromRfc3339(cell.date);
      if (!dt0.isOk) return null;
      const dt = nonnull(dt0.exp(), "RowData: an ok start parse has a value").toUtc();
      const endR = cell.endDate ? DateTime.fromRfc3339(cell.endDate) : null;
      const endDt = endR?.isOk
        ? nonnull(endR.exp(), "RowData: an ok end parse has a value").toUtc()
        : dt;
      return {
        kind: "custom",
        entry: {
          id: cell.id,
          dtr: new DateTime.Range(dt, endDt),
          value: cell.data ?? null,
          style: null,
        },
      };
    }

    if (!isPartial) return null;
    if (lane === "multi") {
      if (cell.id == null) {
        throw new Error(
          `RowData: semantic-multi cell missing id (date="${cell.date}"). ` +
            `Server contract violation — multi-card cells must carry an id.`,
        );
      }
      return {
        kind: "multi",
        entry: {
          date: partial,
          id: cell.id,
          value: cell.data ?? null,
          style: null,
        },
      };
    }
    return {
      kind: "standard",
      entry: { date: partial, id: null, value: cell.data ?? null, style: null },
    };
  }
}

/**
 * Where a cell lives (or should live). One authority for the "which store?"
 * question that the optimistic edit/delete/reconcile paths each used to probe
 * by hand. `standard` carries the date (so the standard store can be written or
 * tombstoned); `custom` carries the raw cell id.
 */
export type CellAddress =
  | { store: "standard"; date: NaiveDate.Partial }
  | { store: "multi"; id: CellId; date: NaiveDate.Partial }
  | { store: "custom"; id: CellId };

/**
 * Typed cell storage for a single row.
 * Parses Network.SheetCell[] into strongly-typed CellEntry / TimepointCellEntry.
 *
 * Standard time-scale cells (ymd-, ym-, y-, yw-) are stored in a SortedDeque
 * keyed by date.start.dse with a secondary Map<cellKey, CellEntry> for O(1)
 * point lookups and O(log n + k) range queries.
 *
 * Custom datetime cells (ISO 8601 with time component) are stored as
 * TimepointCellEntry in a separate Map<string, TimepointCellEntry>.
 */
export class RowData {
  readonly #dateIndex = new SortedDeque<CellEntry, string>(cellKey);
  /** semantic-multi lane: date-keyed cells distinguished by id (many per
   *  date — all-day events). Keyed by raw cell id; the date lives in the
   *  entry, mirroring the sheets `RowCellData._multi`. */
  readonly #multi = new Map<string, CellEntry>();
  readonly #customStore = new Map<string, TimepointCellEntry>();
  /**
   * Per-cell originator stamp. Set by `applyLocalWrite` (the source-store
   * bridge that mirrors local writes into this cache). Read by
   * `lastActorFor` so `RowCellCache.subscribe` can pass it as `change.actor`
   * to the `CellHandle.onData` callback. Plugins use this to opt out of
   * self-echo rebinds. Keyed by the same `cellKey` shape as `dateIndex`.
   */
  readonly #lastActors = new Map<string, Actor>();

  /**
   * Cell cardinality for the row this RowData belongs to. Drives ParsedCell.parse
   * routing (standard vs custom store).
   *
   * Source of truth: the row type's `RowTypeDescriptor.cellCardinality` in `row-descriptor.ts`.
   * The owning `RowCellCache` derives this once in `applyMeta(row)` from the row's
   * type — there is no public setter that takes an arbitrary value. Defaults
   * to "single" so RowData instances created without an owning handle (tests,
   * derived computations) don't accidentally route into the custom store.
   */
  cardinality: CellCardinality = "single";

  /**
   * Target address — where a write for `(id, date)` SHOULD land, decided by
   * cardinality alone: `multi` rows key cells by id in the custom store,
   * `single` rows by date in the standard store. (Datetime-shape routing is a
   * parse-time concern — `ParsedCell.parse`; the optimistic write path always
   * receives a date-granular `NaiveDate.Partial`.) Mirrors `upsert`'s routing.
   */
  addressOf(id: Option<CellId>, date: NaiveDate.Partial): CellAddress {
    // A date-granular write is SEMANTIC by construction (it carries a date, not
    // a datetime); timepoint writes go through `replaceCustomCell` with a `dtr`,
    // never here. So cardinality alone picks the semantic lane: `multi` →
    // id-distinguished date lane, `single` → the date-keyed standard store.
    if (this.cardinality === "multi") {
      if (id == null) {
        throw new Error(
          `RowData.addressOf: multi-cardinality row requires id (date="${date.toString()}").`,
        );
      }
      requireNonEmptyKey("RowData.addressOf", "id", id);
      return { store: "multi", id, date };
    }
    return { store: "standard", date };
  }

  /**
   * Resolve address — where the cell for `id` CURRENTLY lives: the custom store
   * by id first (fetched datetime cells route there even on single-card rows),
   * else the standard cell at `date` when its value actually holds this id, else
   * `null`. This is the single answer to the "which store?" question that
   * `applyOptimisticEdit` (its `hasCustomCell` probe), `applyOptimisticDelete`
   * (its `removeCustom`-then-`some(...)` guard), and `cellStillHolds` (its dual
   * probe) each computed by hand.
   */
  addressOfExisting(id: CellId, date: NaiveDate.Partial): Option<CellAddress> {
    requireNonEmptyKey("RowData.addressOfExisting", "id", id);
    // Most-specific identity first: timepoint (custom) → semantic-multi → the
    // standard store's value-`.id` scan last (the only by-value probe).
    if (this.#customStore.has(id)) return { store: "custom", id };
    const m = this.#multi.get(id);
    if (m) return { store: "multi", id, date: m.date };
    const std = this.#dateIndex.get(`${date.type}:${date.index}`);
    if (std && (std.value as Option<Partial<{ id: string }>>)?.id === id) {
      return { store: "standard", date: std.date };
    }
    return null;
  }

  /**
   * Whether `applyBatch` of a row of `cardinality` can place `cell`: a cell
   * of an id-keyed lane (a timepoint cell, or a date-keyed cell of a
   * multi-cardinality row) must
   * carry its id; a date-keyed cell of a single-cardinality row always
   * places. A write's answer is filtered through this before it is
   * reconciled (`CellAccessor.reconcileAnswer`), so an accepted write never
   * rejects over a malformed answer.
   */
  static admits(cell: RowData.Cell, cardinality: CellCardinality): boolean {
    const semantic = NaiveDate.Partial.parseOptional(cell.date) != null;
    const lane = CellLane.of(cardinality, semantic ? "semantic" : "timepoint");
    return lane === "index" || cell.id != null;
  }

  /**
   * Upserts cells from a network batch response. Existing entries for the
   * same date key are replaced; entries for other dates are preserved.
   */
  applyBatch(cells: readonly RowData.Cell[]): void {
    for (const cell of cells) {
      const parsed = ParsedCell.parse(cell, this.cardinality);
      if (!parsed) continue;
      if (parsed.kind === "standard") {
        this.#dateIndex.upsert(parsed.entry.date.start.dse, parsed.entry);
        // Sync/server writes don't carry a local actor — clear any stale
        // local stamp so the next `onData` fire correctly treats this as
        // a remote update (not a self-echo). If a subsequent local write
        // happens, `applyLocalWrite` re-stamps.
        this.#lastActors.delete(cellKey(parsed.entry));
      } else if (parsed.kind === "multi") {
        this.#multi.set(parsed.entry.id, parsed.entry);
        this.#lastActors.delete(`id:${parsed.entry.id}`);
      } else {
        this.#customStore.set(parsed.entry.id, parsed.entry);
        this.#lastActors.delete(`id:${parsed.entry.id}`);
      }
    }
  }

  /**
   * Upserts a batch and returns whether any stored entry actually changed.
   * Once a change is detected, skips further comparisons and applies the rest directly.
   */
  applyBatchWithChange(cells: readonly RowData.Cell[]): boolean {
    for (const cell of cells) {
      if (!this.#cellChanged(cell)) continue;
      this.applyBatch(cells);
      return true;
    }
    return false;
  }

  #cellChanged(cell: RowData.Cell): boolean {
    const parsed = ParsedCell.parse(cell, this.cardinality);
    if (!parsed) return false;
    if (parsed.kind === "standard") {
      const existing = this.#dateIndex.get(cellKey(parsed.entry));
      return !existing ||
        !RowData.#jsonEquals(existing.value, cell.data ?? null);
    }
    if (parsed.kind === "multi") {
      const existing = this.#multi.get(parsed.entry.id);
      return !existing ||
        existing.date.start.dse !== parsed.entry.date.start.dse ||
        !RowData.#jsonEquals(existing.value, cell.data ?? null);
    }
    const existing = this.#customStore.get(parsed.entry.id);
    if (!existing) return true;
    return (
      existing.dtr.start.mse !== parsed.entry.dtr.start.mse ||
      existing.dtr.end.mse !== parsed.entry.dtr.end.mse ||
      !RowData.#jsonEquals(existing.value, cell.data ?? null)
    );
  }

  /**
   * Inserts pre-parsed CellEntry objects directly (used by formula compute path).
   */
  insertEntries(entries: CellEntry[]): void {
    for (const entry of entries) {
      this.#dateIndex.upsert(entry.date.start.dse, entry);
    }
  }

  /**
   * Replace only the semantic cells in `range` with freshly computed entries.
   * Formula refreshes run per visible column/range; replacing the whole RowData
   * store lets whichever range finishes last blank the other visible ranges.
   */
  replaceEntriesInRange(range: NaiveDate.Range, entries: CellEntry[]): void {
    requireNaiveDateRange("RowData.replaceEntriesInRange", "range", range);
    const standardEntries = this.#dateIndex.getInRange(
      range.start.dse,
      range.end.dse,
    );
    for (const entry of standardEntries) {
      this.#dateIndex.deleteByKey(cellKey(entry));
      this.#lastActors.delete(cellKey(entry));
    }
    if (this.cardinality === "multi") {
      for (const [id, entry] of this.#multi) {
        if (!range.contains(entry.date.start)) continue;
        this.#multi.delete(id);
        this.#lastActors.delete(`id:${id}`);
      }
    }
    this.insertEntries(entries);
  }

  /**
   * Upserts a single cell by date (used by applyExternalEdit).
   * For multi-cardinality rows the caller must supply `id`
   * (mirrors the server's `Cell::ranged(_, _, _, Some(id))`
   * requirement); for single-card rows `id` must be `null`.
   */
  upsert(date: NaiveDate.Partial, data: LocalCellValue, id: Option<string>): void {
    if (this.cardinality === "multi") {
      if (id == null) {
        throw new Error(
          `RowData.upsert: multi-cardinality row requires id (date="${date.toString()}").`,
        );
      }
      requireNonEmptyKey("RowData.upsert", "id", id);
      // If this id is already a TIMEPOINT cell (a scheduling slot in the
      // custom store), a day-granular write must NOT demote it to the
      // semantic lane — the source-store bridge mirrors writes day-keyed, and
      // collapsing a slot's precise `dtr` to midnight is the "slot doesn't
      // show after reload" bug. Keep it in the custom store: same-day → keep
      // the existing range + update the value; a genuine day move → re-key to
      // the new day. (Timed events take the explicit `replaceCustomCell` path,
      // not this one.)
      const existingCustom = this.#customStore.get(id);
      if (existingCustom != null) {
        const dayRange = date.toRange();
        const existingDse = existingCustom.dtr.start.date.dse;
        const dtr = existingDse >= dayRange.start.dse &&
            existingDse <= dayRange.end.dse
          ? existingCustom.dtr
          : (() => {
            const dt = DateTime.fromDse(date.start.dse, Utc);
            return new DateTime.Range(dt, dt);
          })();
        this.#customStore.set(id, { id: CellId.of(id), dtr, value: data ?? null, style: null });
        this.#lastActors.delete(`id:${id}`);
        return;
      }
      // Otherwise it's a NEW semantic-multi cell (all-day event, date-keyed
      // slot) → the id-keyed date lane. Re-writing the same id updates its
      // date (a move) + value in place; the lane is id-keyed so no stale cell.
      this.#multi.set(id, { date, id: CellId.of(id), value: data ?? null, style: null });
      // Untagged write: clear any stale local actor stamp. Callers that
      // want to preserve the originator's actor go through
      // `applyLocalWrite` instead.
      this.#lastActors.delete(`id:${id}`);
      return;
    }
    const existing = this.#dateIndex.get(`${date.type}:${date.index}`);
    const entry: CellEntry = {
      date: existing?.date ?? date,
      id: null,
      value: data ?? null,
      style: null,
    };
    this.#dateIndex.upsert(date.start.dse, entry);
    this.#lastActors.delete(`${date.type}:${date.index}`);
  }

  /**
   * Clear a date-keyed cell from the standard store, independent of
   * cardinality. `upsert` routes multi-cardinality writes to the id-keyed
   * custom store and rejects a missing id, so it cannot clear a standard
   * cell on a multi-card row — but a standard cell can exist on a
   * multi-card row when it was written while the row was single (e.g. an
   * optimistic insert) and the row later re-resolved to `multi` via
   * `applyMeta`. Writing a null value is how the standard store marks a
   * cell absent (rebuild drops null-valued entries). Pair with
   * `addressOfExisting` returning a `standard` address.
   */
  clearStandardCell(date: NaiveDate.Partial): void {
    const existing = this.#dateIndex.get(`${date.type}:${date.index}`);
    const entry: CellEntry = {
      date: existing?.date ?? date,
      id: null,
      value: null,
      style: null,
    };
    this.#dateIndex.upsert(date.start.dse, entry);
    this.#lastActors.delete(`${date.type}:${date.index}`);
  }

  /**
   * Mirrors a local cell write into this cache, with the originator's
   * actor stamp. Used by the source-store → `_fetched` bridge so that
   * `CellHandle.onData` callbacks see `change.actor` and can opt out of
   * self-echo rebinds. Same upsert shape as `upsert`, plus the actor
   * recorded in `lastActors`.
   */
  applyLocalWrite(
    date: NaiveDate.Partial,
    data: LocalCellValue,
    actor: Actor,
    id: Option<string>,
  ): void {
    this.upsert(date, data, id ?? null);
    if (id != null && this.cardinality === "multi") {
      this.#lastActors.set(`id:${id}`, actor);
    } else {
      this.#lastActors.set(`${date.type}:${date.index}`, actor);
    }
  }

  /**
   * Stamp the cells of a write's answer (already applied with
   * `applyBatch`, which clears their stamps) with the writer's `actor`, so
   * `CellHandle.onData` reports the writer as `change.actor` for a cell
   * the server minted (an `add`, a timepoint write): a date-keyed cell
   * under its column, a multi-cardinality one also under its id, a
   * timepoint one under its id.
   */
  stampAnswer(cells: readonly RowData.Cell[], actor: Actor): void {
    for (const cell of cells) {
      const parsed = ParsedCell.parse(cell, this.cardinality);
      if (!parsed) continue;
      if (parsed.kind === "standard") {
        this.#lastActors.set(cellKey(parsed.entry), actor);
      } else if (parsed.kind === "multi") {
        this.#lastActors.set(cellKey(parsed.entry), actor);
        this.#lastActors.set(`id:${parsed.entry.id}`, actor);
      } else {
        this.#lastActors.set(`id:${parsed.entry.id}`, actor);
      }
    }
  }

  /**
   * Returns the actor that most recently wrote to the cell at this date
   * (via `applyLocalWrite`), or `null` if no local write has been
   * recorded. Server-driven sync updates do NOT stamp an actor — they
   * round-trip without local actor identity, so `lastActorFor` returns
   * `null` for sync-only cells. That's intentional: only locally-
   * originated writes participate in self-echo suppression.
   */
  lastActorFor(date: NaiveDate.Partial, id: Option<string>): Option<Actor> {
    if (id != null && this.cardinality === "multi") {
      requireNonEmptyKey("RowData.lastActorFor", "id", id);
      return this.#lastActors.get(`id:${id}`) ?? null;
    }
    return this.#lastActors.get(`${date.type}:${date.index}`) ?? null;
  }

  /**
   * Returns all standard CellEntry items whose date.start falls within the
   * range (start inclusive, end exclusive). O(log n + k).
   */
  getAllInRange(range: NaiveDate.Range): CellEntry[] {
    requireNaiveDateRange("RowData.getAllInRange", "range", range);
    // Semantic cells home in `dateIndex` (single) or `multi` (id-distinguished).
    // A multi row reads `multi` PLUS any `dateIndex` cells stranded by a
    // single→multi transition (an optimistic write before `applyMeta` set
    // `multi`) so they stay visible + clearable.
    if (this.cardinality === "multi") {
      const out: CellEntry[] = this.#dateIndex
        .getInRange(range.start.dse, range.end.dse)
        .slice();
      for (const entry of this.#multi.values()) {
        if (range.contains(entry.date.start)) out.push(entry);
      }
      return out.sort((a, b) => a.date.start.dse - b.date.start.dse);
    }
    return this.#dateIndex.getInRange(range.start.dse, range.end.dse);
  }

  /** Returns all semantic CellEntry items, sorted by date.start.dse. */
  getAll(): CellEntry[] {
    const out: CellEntry[] = [];
    this.#dateIndex.forEach((_, entry) => out.push(entry));
    if (this.cardinality === "multi") {
      for (const entry of this.#multi.values()) out.push(entry);
      out.sort((a, b) => a.date.start.dse - b.date.start.dse);
    }
    return out;
  }

  /**
   * Returns all TimepointCellEntry items whose date falls within the UTC range
   * (start inclusive, end exclusive).
   */
  getAllTimepointsInRange(range: DateTime.Range<Utc>): TimepointCellEntry[] {
    requireDateTimeRange("RowData.getAllTimepointsInRange", "range", range);
    const out: TimepointCellEntry[] = [];
    for (const entry of this.#customStore.values()) {
      if (
        entry.dtr.start.mse >= range.start.mse &&
        entry.dtr.start.mse < range.end.mse
      ) {
        out.push(entry);
      }
    }
    return out.sort((a, b) => a.dtr.start.mse - b.dtr.start.mse);
  }

  /** Returns all TimepointCellEntry items, sorted by date. */
  getAllCustom(): TimepointCellEntry[] {
    return [...this.#customStore.values()].sort(
      (a, b) => a.dtr.start.mse - b.dtr.start.mse,
    );
  }

  get size(): number {
    return this.#dateIndex.size + this.#multi.size + this.#customStore.size;
  }

  /** Remove ONE semantic-multi cell by its raw id. Returns true if a cell was
   *  present. Counterpart of `removeCustom` for the date-keyed id lane. */
  removeMulti(id: string): boolean {
    requireNonEmptyKey("RowData.removeMulti", "id", id);
    const had = this.#multi.delete(id);
    if (had) this.#lastActors.delete(`id:${id}`);
    return had;
  }

  /**
   * Remove ONE custom cell by its raw id. Returns the removed cell's
   * range, or null when no such cell exists. Single-cell counterpart of
   * `clearCustomInRange` (which would also remove range neighbors).
   */
  removeCustom(id: string): Option<DateTime.Range<Utc>> {
    requireNonEmptyKey("RowData.removeCustom", "id", id);
    const entry = this.#customStore.get(id);
    if (!entry) return null;
    this.#customStore.delete(id);
    this.#lastActors.delete(`id:${id}`);
    return entry.dtr;
  }

  /** Remove custom cells whose start falls within the given UTC range. */
  clearCustomInRange(range: DateTime.Range<Utc>): void {
    requireDateTimeRange("RowData.clearCustomInRange", "range", range);
    for (const [key, entry] of this.#customStore) {
      if (
        entry.dtr.start.mse >= range.start.mse &&
        entry.dtr.start.mse < range.end.mse
      ) {
        this.#customStore.delete(key);
      }
    }
  }

  clear(): void {
    this.#dateIndex.clear();
    this.#multi.clear();
    this.#customStore.clear();
  }

  static #jsonEquals(
    a: Option<Json | CellValue>,
    b: Option<Json | CellValue>,
  ): boolean {
    if (a === b) return true;
    if (a == null || b == null) return a === b;
    if (typeof a === "object" && typeof b === "object") {
      return JSON.stringify(a) === JSON.stringify(b);
    }
    return false;
  }
}

export namespace RowData {
  /**
   * A cell as the store takes it in: the wire cell, whose `data` is either
   * the wire JSON it arrived as or the canonical value an encoding decoded
   * it to (an event with `DateTime`s) or an optimistic write put there.
   * Every `Network.SheetCell` is one.
   */
  export type Cell = Omit<Network.SheetCell, "data"> & {
    readonly data: Option<Json | CellValue>;
  };
}
